import { MapPin } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import { TRANSFER_UNITS_COUNT_PATTERN, usesTransferWizardCardLayout } from "../../../utils/transferPropertyCompanyWizard"
import { splitPropertyAddressFieldClass, splitPropertyAddressLabelClass } from "../../../constants/transferProcessStyles"
import type { TransferPropertyInfoSectionProps } from "./propertyInfoSectionTypes"

function splitAddressColumnClass(useCardLayout: boolean) {
  return useCardLayout
    ? "rounded-lg border border-slate-100 bg-slate-50/50 p-4 sm:p-5"
    : "rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/40 p-5 shadow-sm ring-1 ring-slate-100 sm:p-6"
}

function splitAddressColumnHeadingClass(useCardLayout: boolean) {
  return cn(
    "mb-5 border-b border-slate-100 pb-3 text-base font-semibold tracking-tight text-slate-900",
    useCardLayout && "text-sm",
  )
}

export function TransferPropertySplitAddressSection({ ctx }: TransferPropertyInfoSectionProps) {
  const { fragment, states, register } = ctx
  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const columnClass = splitAddressColumnClass(useCardLayout)
  const columnHeadingClass = splitAddressColumnHeadingClass(useCardLayout)

  const addressGrid = (
    <div
      data-testid="transfer-process-split-addresses-grid"
      className="grid gap-6 lg:grid-cols-2 lg:gap-10"
    >
      <div
        data-testid="transfer-process-split-address-original-column"
        className={columnClass}
      >
        <h3
          data-testid="transfer-process-split-address-original-heading"
          className={columnHeadingClass}
        >
          Original property
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                data-testid="transfer-process-split-original-address-line1-label"
                className={splitPropertyAddressLabelClass}
              >
                Address Line 1<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="street-address"
                data-testid="transfer-process-split-address-line1-input"
                className={splitPropertyAddressFieldClass}
                {...register("property_address")}
              />
            </div>
            <div>
              <label
                data-testid="transfer-process-split-original-address-line2-label"
                className={splitPropertyAddressLabelClass}
              >
                Address Line 2
              </label>
              <input
                type="text"
                autoComplete="address-line2"
                data-testid="transfer-process-split-address-line2-input"
                className={splitPropertyAddressFieldClass}
                {...register("property_address2")}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label
                data-testid="transfer-process-split-original-city-label"
                className={splitPropertyAddressLabelClass}
              >
                City<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                data-testid="transfer-process-split-original-city-input"
                className={splitPropertyAddressFieldClass}
                {...register("property_city")}
              />
            </div>
            <div>
              <label
                data-testid="transfer-process-split-original-state-label"
                className={splitPropertyAddressLabelClass}
              >
                State<span className="text-red-500">*</span>
              </label>
              <select
                data-testid="transfer-process-split-original-state-select"
                className={splitPropertyAddressFieldClass}
                {...register("property_state")}
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.state_code} value={s.state_code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                data-testid="transfer-process-split-original-zip-label"
                className={splitPropertyAddressLabelClass}
              >
                Zipcode<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                data-testid="transfer-process-split-original-zip-input"
                className={splitPropertyAddressFieldClass}
                {...register("property_zipcode")}
              />
            </div>
          </div>
          <div className="max-w-full sm:max-w-[220px]">
            <label
              data-testid="transfer-process-split-original-units-label"
              className={splitPropertyAddressLabelClass}
            >
              # of Units<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="transfer-process-split-original-units-input"
              className={splitPropertyAddressFieldClass}
              {...register("units_count", { pattern: TRANSFER_UNITS_COUNT_PATTERN })}
            />
          </div>
        </div>
      </div>

      <div
        data-testid="transfer-process-split-address-clone-column"
        className={columnClass}
      >
        <h3
          data-testid="transfer-process-split-address-clone-heading"
          className={columnHeadingClass}
        >
          New property
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                data-testid="transfer-process-split-clone-address-line1-label"
                className={splitPropertyAddressLabelClass}
              >
                Address Line 1<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="street-address"
                data-testid="transfer-process-split-clone-address-line1-input"
                className={splitPropertyAddressFieldClass}
                {...register("newpropertyinfo.property_address")}
              />
            </div>
            <div>
              <label
                data-testid="transfer-process-split-clone-address-line2-label"
                className={splitPropertyAddressLabelClass}
              >
                Address Line 2
              </label>
              <input
                type="text"
                autoComplete="address-line2"
                data-testid="transfer-process-split-clone-address-line2-input"
                className={splitPropertyAddressFieldClass}
                {...register("newpropertyinfo.property_address2")}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label
                data-testid="transfer-process-split-clone-city-label"
                className={splitPropertyAddressLabelClass}
              >
                City<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                data-testid="transfer-process-split-clone-city-input"
                className={splitPropertyAddressFieldClass}
                {...register("newpropertyinfo.property_city")}
              />
            </div>
            <div>
              <label
                data-testid="transfer-process-split-clone-state-label"
                className={splitPropertyAddressLabelClass}
              >
                State<span className="text-red-500">*</span>
              </label>
              <select
                data-testid="transfer-process-split-clone-state-select"
                className={splitPropertyAddressFieldClass}
                {...register("newpropertyinfo.property_state")}
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={`n-${s.state_code}`} value={s.state_code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                data-testid="transfer-process-split-clone-zip-label"
                className={splitPropertyAddressLabelClass}
              >
                Zipcode<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                data-testid="transfer-process-split-clone-zip-input"
                className={splitPropertyAddressFieldClass}
                {...register("newpropertyinfo.property_zipcode")}
              />
            </div>
          </div>
          <div className="max-w-full sm:max-w-[220px]">
            <label
              data-testid="transfer-process-split-clone-units-label"
              className={splitPropertyAddressLabelClass}
            >
              # of Units<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="No Of Units"
              data-testid="transfer-process-split-clone-units-input"
              className={splitPropertyAddressFieldClass}
              {...register("newpropertyinfo.units_count", { pattern: TRANSFER_UNITS_COUNT_PATTERN })}
            />
          </div>
        </div>
      </div>
    </div>
  )

  if (useCardLayout) {
    return (
      <SectionCard
        icon={MapPin}
        title="Property Address"
        testId="transfer-process-split-addresses-section"
        titleTestId="transfer-process-split-addresses-section-title"
      >
        {addressGrid}
      </SectionCard>
    )
  }

  return (
    <>
      <h2
        data-testid="transfer-process-split-addresses-section-title"
        className={companyFormStyles.sectionTitle}
      >
        Property Address
      </h2>
      {addressGrid}
    </>
  )
}
